<template>
    <app-layout v-if="isPageReady" printClass="print-referral-listing">
        <common-tabs class="referral-listing-wrapper" :tabs="tabOptions" :activeTab="activeTab" @tab-click="changeTab($event)">
            <template v-slot:referrals>
                <common-content-spacer v-if="activeTab === 'referrals'" height="medium">
                    <common-content-spacer height="small">
                        <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
                        <common-page-header :heading="pageTitle"></common-page-header>
                    </common-content-spacer>

                    <common-page-plain>
                        <common-content-spacer height="medium">
                            <referrals-index-listing
                                :referrals="referrals"
                                :type="$route.params.type"
                                :filterOptions="filterOptions"
                            ></referrals-index-listing>
                            <common-buttons>
                                <common-button
                                    v-if="referrals.length < referralsTotalCount"
                                    :label="$t('pages.referrals.index.buttons.show_more')"
                                    type="primary"
                                    size="large"
                                    @click="showMore()"
                                ></common-button>
                            </common-buttons>
                        </common-content-spacer>
                    </common-page-plain>
                </common-content-spacer>
            </template>
            <template v-slot:pool>
                <referral-pool v-if="activeTab === 'pool'"></referral-pool>
            </template>
        </common-tabs>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsIndexListing from 'components/page-elements/referrals/ReferralsIndexListing'
    import DemoReferral from 'services/DemoReferral'
    import ReferralPool from '../pool/ReferralPool.vue'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin],

        components: {
            AppLayout,
            ReferralsIndexListing,
            ReferralPool,
        },

        data() {
            return {
                referrals: [],
                offset: 0,
                referralsTotalCount: 0,
                activeTab: undefined,
                allFilterOptions: [
                    {
                        label: this.$t('pages.referrals.index.filters.pending_confirmation'),
                        status: 'CREATED',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.patient_visit_pending'),
                        status: 'PATIENT_VISIT_PENDING',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.pending_insurance_auth'),
                        status: 'INSURANCE_AUTH_PENDING',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.pending_consultation_report'),
                        status: 'CONSULTATION_NOTES_PENDING',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.to_be_rescheduled'),
                        status: 'RESCHEDULE_REQUESTED',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.out_of_availability'),
                        status: 'RESCHEDULE_AVAILABILITY_CHANGE',
                        value: true,
                        state: 'open',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.declined'),
                        status: 'DECLINED',
                        value: true,
                        state: 'closed',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.cancelled'),
                        status: 'CANCELLED',
                        value: true,
                        state: 'closed',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.no_show'),
                        status: 'NO_SHOW',
                        value: true,
                        state: 'closed',
                    },
                    {
                        label: this.$t('pages.referrals.index.filters.completed'),
                        status: 'COMPLETED',
                        value: true,
                        state: 'closed',
                    },
                ],
                filterOptions: [],
            }
        },

        provide() {
            return {
                isReferralPool: false,
            }
        },

        loadData(self) {
            if (self.$auth.isUserPracticeDemo()) {
                if (self.$route.params.type === 'made') {
                    return {
                        referrals: [],
                    }
                } else {
                    return {
                        referrals: DemoReferral.getDemoReferral(),
                    }
                }
            } else {
                return {
                    referrals: ReferralsAPI.getList(
                        self.$route.params.type,
                        self.$auth.getActiveMember()?.id ? self.$auth.getActiveMember()?.id : self.$auth.getUser().id,
                        self.$route.query,
                    ),
                    offset: 0,
                }
            }
        },

        computed: {
            pageTitle() {
                return this.$t(`pages.referrals.index.titles.${this.$route.params.type}`)
            },

            tabOptions() {
                const tabs = [
                    {
                        displayName: this.$t('layouts.app.topnav.direct_referral'),
                        name: 'referrals',
                        icon: 'direct-referral',
                    },
                ]
                if (!this.$auth.isPracticeAncillary() || this.$route.params.type === 'received') {
                    tabs.push({
                        displayName: this.$t('layouts.app.topnav.match_referral'),
                        name: 'pool',
                        icon: 'match-referral',
                    })
                }
                return tabs
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.index.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    { label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`) },
                ]
            },
        },

        methods: {
            loadReferrals(params) {
                ReferralsAPI.getList(
                    this.$route.params.type,
                    this.$auth.getActiveMember() && this.$auth.getActiveMember()?.id
                        ? this.$auth.getActiveMember()?.id
                        : this.$auth.getUser().id,
                    params,
                ).then((res) => {
                    this.referrals = this.referrals.concat(res)
                })
            },

            changeTab(event) {
                this.$router.push({ name: `referrals.${this.$route.params.type}.${event}` })
                this.activeTab = event
            },

            showMore() {
                this.offset = this.offset + Number(this.$route.query.limit)
                this.loadReferrals({ ...this.$route.query, offset: this.offset })
            },

            setFilterOptions() {
                const state = this.$route.query.state
                this.filterOptions =
                    state && state !== 'all'
                        ? this.getResetFilterOptions().filter((option) => option.state === state)
                        : this.getResetFilterOptions()
            },

            getResetFilterOptions() {
                return this.allFilterOptions.map((option) => {
                    option.value = true
                    return option
                })
            },
        },

        watch: {
            referrals() {
                if (this.referrals._headers) {
                    this.referralsTotalCount = this.referrals._headers['x-total-count']
                }
            },

            '$route.params.type'() {
                this.changeTab(this.$route.params.tabType)
            },
            '$route.query.state'() {
                this.setFilterOptions()
            },
        },

        mounted() {
            this.setFilterOptions()
            if (this.$route.params.tabType) {
                this.changeTab(this.$route.params.tabType)
            }
        },
    }
</script>
<style lang="scss">
    .referral-listing-wrapper {
        .tabcontent {
            margin-top: 30px;
        }
    }
</style>
